/**
 * Utility functions for AMFI (Attributes, Metrics, Facts, Insights) components
 */

/**
 * Parse selected text into structured data for AMFI items
 * Detects patterns and formats data appropriately for different AMFI types
 *
 * @param {string} text - The selected text to parse
 * @param {string} type - The AMFI type (attribute, metric, fact, insight)
 * @returns {Object} - Parsed fields appropriate for the AMFI type
 */
export const parseSelectedText = (text, type) => {
	// Initialize with default structure
	let result = {
		// Use generic fields first
		name: `${type} from selection`,
		value: text.trim(),
	};

	// Clean text of any problematic characters
	// eslint-disable-next-line no-control-regex
	const cleanText = text.replace(/[\u0000-\u001F\u007F-\u009F]/g, '').trim();

	// Try to detect if this is structured data from deal information
	const dealInfoMatch = cleanText.match(/[*+_-]+([\w_]+)\s*[:=]+\s*(.+)/);
	if (dealInfoMatch) {
		// Handle structured data format like "**deal_created_date**: 2023-12-04"
		result.name = dealInfoMatch[1].replace(/[_]+/g, ' ').trim();
		result.value = dealInfoMatch[2].trim();
	} else {
		// Common patterns to detect
		// Pattern 1: "Name: Value" format
		const colonMatch = cleanText.match(/([^:]+):\s*(.*)/);
		if (colonMatch) {
			result.name = colonMatch[1].trim();
			result.value = colonMatch[2].trim();
		} else {
			// Pattern 2: For metrics, try to extract numeric values
			if (type.toLowerCase() === 'metric') {
				// First try to match currency patterns like "$18.5M"
				const currencyMatch = cleanText.match(
					/[$€£¥]?([\d,.]+)\s*([KkMmBbTt%]|million|billion|trillion)?\b/i
				);
				if (currencyMatch) {
					// Extract the numeric part and clean it
					result.value = currencyMatch[1].replace(/,/g, '').trim();

					// Try to identify what this metric represents
					// Look at the rest of the text for a descriptor
					const descriptor = cleanText
						.replace(
							/[$€£¥]?[\d,.]+\s*([KkMmBbTt%]|million|billion|trillion)?\b/i,
							''
						)
						.trim();
					result.name = descriptor || 'Value';

					// If we found a unit marker, store it as unit
					if (currencyMatch[2]) {
						result.unit = currencyMatch[2].trim();
					}
				} else {
					// Try the original approach - numbers followed by units or descriptors
					const numericMatch = cleanText.match(/([\d,.]+)\s*([^\d,.]+)/);
					if (numericMatch) {
						result.name = numericMatch[2].trim();
						result.value = numericMatch[1].replace(/,/g, '').trim();
					}
				}
			}
		}
	}

	// Add type-specific fields
	if (type.toLowerCase() === 'metric') {
		// For metrics, adapt the fields to match expected structure
		result = {
			...result,
			metric_name: result.name,
		};

		// Extract only numeric values for metrics
		if (result.value) {
			// Remove all non-numeric characters except for decimal points
			result.value = result.value.replace(/[^\d.]/g, '');

			// Handle case of multiple decimal points
			const parts = result.value.split('.');
			if (parts.length > 2) {
				// Keep only the first decimal point
				result.value = parts[0] + '.' + parts.slice(1).join('');
			}
		}
	} else if (
		type.toLowerCase() === 'fact' ||
		type.toLowerCase() === 'insight'
	) {
		// For facts and insights, use content field
		result = {
			...result,
			// Keep name for reference
			content: result.value,
		};
	}

	return result;
};
